/** Cart drawer UI — discount/gift card, qty-minus removes at 0 (Hydrogen parity). */

const CART_DISCOUNT_MSG = {
  discountEmpty: 'Enter a discount code.',
  discountInvalid: 'That discount code is not valid for this cart.',
  discountApplied: 'Discount applied.',
  discountRemoved: 'Discount removed.',
  giftCardEmpty: 'Enter a gift card code.',
  giftCardSaved: 'Gift card saved. Apply the balance at checkout payment.',
  giftCardRemoved: 'Gift card removed.',
  error: 'Something went wrong. Please try again.',
};

function cartRoot() {
  return (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
}

function cartUpdateUrl() {
  return (typeof routes !== 'undefined' && routes?.cart_update_url) || '/cart/update';
}

async function fetchCartJson() {
  const res = await fetch(`${cartRoot()}.js`, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error('cart fetch failed');
  return res.json();
}

function normalizeCode(code) {
  return String(code || '').trim();
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatCartMoney(cents) {
  if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
    return Shopify.formatMoney(cents);
  }
  return `$${(Number(cents) / 100).toFixed(2)}`;
}

function updateDrawerSubtotal(cart) {
  const el = document.querySelector('#CartDrawer .cart-checkout-actions__subtotal-amount');
  if (el && cart?.total_price != null) {
    el.textContent = formatCartMoney(cart.total_price);
  }
}

function renderAppliedDiscountsFromCart(root, cart) {
  const panel = getDiscountPanel(root);
  const form = root?.querySelector('[data-cart-discount-form]');
  if (!panel || !form) return;

  panel.querySelectorAll('[data-applied-discount-js]').forEach((row) => row.remove());

  collectDiscountCodes(cart).forEach((code) => {
    const alreadyRendered = [...panel.querySelectorAll('[data-applied-discount] code')].some(
      (node) => node.textContent.trim().toUpperCase() === code.toUpperCase()
    );
    if (alreadyRendered) return;

    const row = document.createElement('div');
    row.className = 'cart-discount cart-discount--applied';
    row.setAttribute('data-applied-discount', '');
    row.setAttribute('data-applied-discount-js', '');
    row.setAttribute('role', 'group');
    row.innerHTML = `<code>${escapeHtml(code)}</code><button type="button" class="cart-discount__remove" data-cart-discount-remove="${escapeHtml(code)}" aria-label="Remove discount ${escapeHtml(code)}">Remove</button>`;
    panel.insertBefore(row, form);
  });
}

async function syncDrawerAfterCartChange() {
  await refreshCartDrawer();
  const cart = await fetchCartJson();
  const root = document.querySelector('#CartDrawer [data-cart-discount-root]');
  renderAppliedDiscountsFromCart(root, cart);
  updateDrawerSubtotal(cart);
  syncDiscountPanelVisibility(root, true);
  return { cart, root };
}

function collectDiscountCodes(cart) {
  const codes = new Set();
  cart?.cart_level_discount_applications?.forEach((app) => {
    if (app?.title) codes.add(app.title);
  });
  cart?.items?.forEach((item) => {
    item?.discounts?.forEach((discount) => {
      if (discount?.title) codes.add(discount.title);
    });
  });
  return [...codes];
}

function discountIsApplied(cart, code) {
  const target = normalizeCode(code).toUpperCase();
  if (!target) return false;
  return collectDiscountCodes(cart).some((entry) => entry.toUpperCase() === target);
}

async function postCartUpdate(body) {
  const res = await fetch(cartUpdateUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('cart update failed');
  return res.json();
}

async function refreshCartDrawer() {
  const res = await fetch(`${cartRoot()}?section_id=cart-drawer`);
  if (!res.ok) throw new Error('section fetch failed');
  const html = await res.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const nextInner = doc.querySelector('#CartDrawer .drawer__inner');
  const currentInner = document.querySelector('#CartDrawer .drawer__inner');
  if (nextInner && currentInner) {
    currentInner.innerHTML = nextInner.innerHTML;
  }
  syncCartDrawerMinusButtons();
  if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount' });
  }
}

function getDiscountRoot(el) {
  return el?.closest('[data-cart-discount-root]');
}

function getDiscountPanel(root) {
  return root?.querySelector('[data-cart-discount-panel]');
}

function syncDiscountPanelVisibility(root, forceOpen) {
  if (!root) return;
  const btn = root.querySelector('[data-cart-discount-toggle]');
  const panel = getDiscountPanel(root);
  const icon = root.querySelector('[data-cart-discount-icon]');
  if (!btn || !panel) return;

  const isOpen = forceOpen === true ? true : forceOpen === false ? false : btn.getAttribute('aria-expanded') === 'true';
  btn.setAttribute('aria-expanded', String(isOpen));
  panel.hidden = !isOpen;
  if (icon) icon.textContent = isOpen ? '−' : '+';
}

function showDiscountFeedback(root, message, isError = false) {
  const feedback = root?.querySelector('[data-cart-discount-feedback]');
  if (!feedback) return;
  feedback.textContent = message;
  feedback.hidden = !message;
  feedback.classList.toggle('cart-discount-toggle__feedback--error', isError);
}

function setDiscountBusy(root, busy) {
  root?.querySelectorAll('button, input').forEach((el) => {
    if (busy) el.setAttribute('disabled', 'disabled');
    else el.removeAttribute('disabled');
  });
}

async function applyDiscountCode(code) {
  const normalized = normalizeCode(code);
  if (!normalized) throw new Error('empty');

  const cart = await fetchCartJson();
  const existing = collectDiscountCodes(cart);
  const merged = [...new Set([...existing, normalized])].join(',');

  await postCartUpdate({ discount: merged });
  const updated = await fetchCartJson();
  if (!discountIsApplied(updated, normalized)) {
    throw new Error('invalid');
  }
  return updated;
}

async function removeDiscountCode(code) {
  const target = normalizeCode(code).toUpperCase();
  const cart = await fetchCartJson();
  const remaining = collectDiscountCodes(cart).filter((entry) => entry.toUpperCase() !== target);
  await postCartUpdate({ discount: remaining.join(',') });
}

async function saveGiftCardCode(code) {
  const normalized = normalizeCode(code);
  if (!normalized) throw new Error('empty');
  await postCartUpdate({ attributes: { gift_card_code: normalized } });
}

async function removeGiftCardCode() {
  await postCartUpdate({ attributes: { gift_card_code: '' } });
}

function syncCartDrawerMinusButtons(root = document) {
  root.querySelectorAll('#CartDrawer quantity-input').forEach((qi) => {
    const input = qi.querySelector('.quantity__input');
    const minus = qi.querySelector(".quantity__button[name='minus']");
    if (!input || !minus) return;
    const val = parseInt(input.value, 10) || 0;
    const disableMinus = val <= 0;
    minus.classList.toggle('disabled', disableMinus);
    minus.toggleAttribute('disabled', disableMinus);
  });
}

document.addEventListener(
  'click',
  (event) => {
    const minus = event.target.closest('#CartDrawer .quantity__button[name="minus"]');
    if (!minus || minus.hasAttribute('disabled')) return;

    const input = minus.closest('quantity-input')?.querySelector('.quantity__input');
    if (!input) return;

    const val = parseInt(input.value, 10) || 0;
    if (val > 1) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    const cartItems = minus.closest('cart-drawer-items');
    const lineIndex = input.dataset.index;
    if (cartItems && lineIndex) {
      cartItems.updateQuantity(lineIndex, 0, event);
    }
  },
  true
);

document.addEventListener('click', (event) => {
  const btn = event.target.closest('[data-cart-discount-toggle]');
  if (!btn || !btn.closest('#CartDrawer')) return;
  const root = getDiscountRoot(btn);
  if (!root) return;

  const isOpen = btn.getAttribute('aria-expanded') === 'true';
  syncDiscountPanelVisibility(root, !isOpen);
});

document.addEventListener('click', async (event) => {
  const removeDiscountBtn = event.target.closest('[data-cart-discount-remove]');
  if (removeDiscountBtn?.closest('#CartDrawer')) {
    event.preventDefault();
    const root = getDiscountRoot(removeDiscountBtn);
    const code = removeDiscountBtn.getAttribute('data-cart-discount-remove');
    setDiscountBusy(root, true);
    showDiscountFeedback(root, '');
    try {
      await removeDiscountCode(code);
      const { root: nextRoot } = await syncDrawerAfterCartChange();
      showDiscountFeedback(nextRoot, CART_DISCOUNT_MSG.discountRemoved);
    } catch {
      showDiscountFeedback(root, CART_DISCOUNT_MSG.error, true);
    } finally {
      setDiscountBusy(root, false);
    }
    return;
  }

  const removeGiftBtn = event.target.closest('[data-cart-gift-card-remove]');
  if (removeGiftBtn?.closest('#CartDrawer')) {
    event.preventDefault();
    const root = getDiscountRoot(removeGiftBtn);
    setDiscountBusy(root, true);
    showDiscountFeedback(root, '');
    try {
      await removeGiftCardCode();
      const { root: nextRoot } = await syncDrawerAfterCartChange();
      showDiscountFeedback(nextRoot, CART_DISCOUNT_MSG.giftCardRemoved);
    } catch {
      showDiscountFeedback(root, CART_DISCOUNT_MSG.error, true);
    } finally {
      setDiscountBusy(root, false);
    }
  }
});

document.addEventListener('submit', async (event) => {
  const discountForm = event.target.closest('[data-cart-discount-form]');
  if (discountForm?.closest('#CartDrawer')) {
    event.preventDefault();
    const root = getDiscountRoot(discountForm);
    const input = discountForm.querySelector('[data-cart-discount-input]');
    const code = normalizeCode(input?.value);
    if (!code) {
      showDiscountFeedback(root, CART_DISCOUNT_MSG.discountEmpty, true);
      input?.focus();
      return;
    }

    setDiscountBusy(root, true);
    showDiscountFeedback(root, '');
    try {
      await applyDiscountCode(code);
      const { root: nextRoot } = await syncDrawerAfterCartChange();
      showDiscountFeedback(nextRoot, CART_DISCOUNT_MSG.discountApplied);
    } catch (err) {
      if (err?.message === 'invalid') {
        showDiscountFeedback(root, CART_DISCOUNT_MSG.discountInvalid, true);
      } else if (err?.message === 'empty') {
        showDiscountFeedback(root, CART_DISCOUNT_MSG.discountEmpty, true);
      } else {
        showDiscountFeedback(root, CART_DISCOUNT_MSG.error, true);
      }
    } finally {
      setDiscountBusy(root, false);
    }
    return;
  }

  const giftForm = event.target.closest('[data-cart-gift-card-form]');
  if (giftForm?.closest('#CartDrawer')) {
    event.preventDefault();
    const root = getDiscountRoot(giftForm);
    const input = giftForm.querySelector('[data-cart-gift-card-input]');
    const code = normalizeCode(input?.value);
    if (!code) {
      showDiscountFeedback(root, CART_DISCOUNT_MSG.giftCardEmpty, true);
      input?.focus();
      return;
    }

    setDiscountBusy(root, true);
    showDiscountFeedback(root, '');
    try {
      await saveGiftCardCode(code);
      const { root: nextRoot } = await syncDrawerAfterCartChange();
      showDiscountFeedback(nextRoot, CART_DISCOUNT_MSG.giftCardSaved);
    } catch {
      showDiscountFeedback(root, CART_DISCOUNT_MSG.error, true);
    } finally {
      setDiscountBusy(root, false);
    }
  }
});

document.addEventListener('click', (event) => {
  if (event.target.closest('#CartDrawer .quantity__button[name="plus"]')) {
    queueMicrotask(() => syncCartDrawerMinusButtons());
  }
});

document.addEventListener('DOMContentLoaded', () => {
  syncCartDrawerMinusButtons();
  document.querySelectorAll('#CartDrawer [data-cart-discount-root]').forEach((root) => {
    syncDiscountPanelVisibility(root, false);
  });
});

if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
    requestAnimationFrame(() => syncCartDrawerMinusButtons());
  });
}

/*
 * Clarity P0 (leftovers1001, founder go 2026-10-01): Back from Shopify checkout.
 * Chrome either restores the page from the back/forward cache or re-renders the HTML it cached on the
 * first visit. Either way the drawer and the header bubble show the cart as it was when that HTML was
 * served (repro: item added, checkout, Back -> drawer says "you haven't added anything yet", bubble 0).
 * On every history return we (1) make sure the drawer stylesheets are applied, re-attaching any that
 * did not take, so the drawer can never paint as plain text, and (2) re-render the drawer and bubble
 * from the live cart. Read-only: GET of the cart sections, no cart writes.
 */
function balDrawerLooksUnstyled() {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return false;
  return getComputedStyle(drawer).position !== 'fixed';
}

function balReattachDrawerStyles() {
  document.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
    if (!/(cart|drawer|totals|price|discounts)[^/]*\.css/.test(link.href)) return;
    if (link.sheet && !balDrawerLooksUnstyled()) return;
    const fresh = link.cloneNode();
    fresh.media = 'all';
    fresh.removeAttribute('onload');
    link.after(fresh);
  });
}

async function balResyncCartAfterHistoryNav() {
  if (balDrawerLooksUnstyled()) balReattachDrawerStyles();
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const res = await fetch(`${root}?sections=cart-drawer,cart-icon-bubble`, { cache: 'no-store', headers: { Accept: 'application/json' } });
  if (!res.ok) return;
  const sections = await res.json();
  const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
  const drawer = document.querySelector('cart-drawer');
  const current = document.getElementById('CartDrawer');
  if (sections['cart-drawer'] && drawer && current) {
    const doc = parse(sections['cart-drawer']);
    const next = doc.getElementById('CartDrawer');
    const nextDrawer = doc.querySelector('cart-drawer');
    if (next) {
      current.innerHTML = next.innerHTML;
      drawer.classList.toggle('is-empty', !!(nextDrawer && nextDrawer.classList.contains('is-empty')));
      current.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
    }
  }
  const bubble = document.getElementById('cart-icon-bubble');
  if (sections['cart-icon-bubble'] && bubble) {
    const next = parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
    if (next) bubble.innerHTML = next.innerHTML;
  }
  syncCartDrawerMinusButtons();
  document.querySelectorAll('#CartDrawer [data-cart-discount-root]').forEach((r) => syncDiscountPanelVisibility(r, false));
}

window.addEventListener('pageshow', (event) => {
  const nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
  if (!event.persisted && !(nav && nav.type === 'back_forward')) return;
  balResyncCartAfterHistoryNav().catch(() => {});
});
